import { useProjectStore } from '@/store/projectStore';
import { useMapStore } from '@/store/remapStore';
import { useTemplateStore } from '@/store/templateStore';
import type { SearchMatch } from '@/store/remapStore';
import type {
  ActivitySummary,
  ActivityTemplateSummary,
  MirroredActivitySummary,
  Project,
  ProjectMapTemplate,
} from '@beeboard/common';

type DataStructure = string | Record<string, unknown> | unknown[] | unknown;

interface SearchResult {
  found: boolean;
  matches: Array<{
    property: string;
    text: string;
    category:
      | 'Activity'
      | 'Completion Criteria'
      | 'Action'
      | 'Tag'
      | 'Topic'
      | 'Link'
      | 'Document';
    objectPath?: string;
    objectId?: string;
    objectTitle?: string;
  }>;
}

export function formatPropertyName(property: string): string {
  // Map to existing translation keys
  const propertyTranslations: Record<string, string> = {
    description: $t('activity.description'),
    tags: $t('activity.tags'),
    notes: $t('activity.notes'),
    title: $t('activity.title'),
    responsible: $t('activity.responsible'),
    first_name: $t('users.firstName'),
    last_name: $t('users.lastName'),
    username: $t('users.userName'),
    content: $t('completionCriterion.description'),
    link: $t('mimeTypeOptions.url'),
    document: $t('files.document'),
  };

  return (
    propertyTranslations[property] ||
    property.charAt(0).toUpperCase() + property.slice(1)
  );
}

export function useSearch() {
  const mapStore = useMapStore();
  const projectStore = useProjectStore();
  const templateStore = useTemplateStore();

  // Get tags, links, and documents based on current context
  const tags = computed(() => {
    const store = mapStore.isTemplate ? templateStore : projectStore;

    return store.allTags(mapStore.mapId);
  });

  const links = computed(() => {
    const store = mapStore.isTemplate ? templateStore : projectStore;

    return store.allLinks(mapStore.mapId);
  });

  const documents = computed(() => {
    const store = mapStore.isTemplate ? templateStore : projectStore;

    return store.allDocuments(mapStore.mapId);
  });
  function shouldSkipProperty(key: string, _path: string): boolean {
    // Skip properties that would cause circular references or false categorization
    const skipProperties = [
      'referenced_in', // Links/Documents referencing back to activities
      'id',
      'workstream',
      'phase',
      'predecessors',
      'milestone',
      'function_id',
      'parent',
      'responsible',
    ];

    const shouldSkip = skipProperties.includes(key);

    return shouldSkip;
  }

  function determineCategory(
    path: string,
    key?: string,
    tagObject?: { topic?: boolean },
  ):
    | 'Activity'
    | 'Completion Criteria'
    | 'Action'
    | 'Tag'
    | 'Topic'
    | 'Link'
    | 'Document' {
    if (path.includes('completion_criteria')) {
      return 'Completion Criteria';
    }
    if (path.includes('actions')) {
      return 'Action';
    }
    if (path.includes('tags') || key === 'tags') {
      // Check if this is a topic based on the topic property
      if (tagObject && tagObject.topic === true) {
        return 'Topic';
      }

      return 'Tag';
    }
    if (path.includes('links') || key === 'links') {
      return 'Link';
    }
    if (path.includes('documentsIds') || key === 'documentsIds') {
      return 'Document';
    }
    if (path.includes('documents') || key === 'documents') {
      return 'Document';
    }

    return 'Activity';
  }

  function getObjectInfo(
    path: string,
    rootActivity:
      | ActivitySummary
      | ActivityTemplateSummary
      | MirroredActivitySummary,
  ): { objectId?: string; objectTitle?: string } {
    // Extract object ID and title from nested objects by parsing the path
    if (
      path.includes('completion_criteria[') &&
      'completion_criteria' in rootActivity &&
      rootActivity.completion_criteria
    ) {
      const match = path.match(/completion_criteria\[(\d+)\]/);
      if (match) {
        const index = parseInt(match[1]);
        const criterion = rootActivity.completion_criteria[index];
        if (criterion && typeof criterion === 'object' && criterion !== null) {
          const criterionObj = criterion as {
            id?: string;
            title?: string;
            description?: string;
          };

          return {
            objectId: criterionObj.id,
            objectTitle: criterionObj.title || criterionObj.description,
          };
        }
      }
    }

    if (
      path.includes('actions[') &&
      'actions' in rootActivity &&
      rootActivity.actions
    ) {
      const match = path.match(/actions\[(\d+)\]/);
      if (match) {
        const index = parseInt(match[1]);
        const action = rootActivity.actions[index];
        if (action && typeof action === 'object' && action !== null) {
          const actionObj = action as {
            id?: string;
            title?: string;
            description?: string;
          };

          return {
            objectId: actionObj.id,
            objectTitle: actionObj.title || actionObj.description,
          };
        }
      }
    }

    // Tags and Links are handled differently in the recursive search, so we'll handle this case separately

    return {};
  }

  function recursiveSearchWithContext(
    item: DataStructure,
    searchText: string,
    key?: string,
    path: string = '',
    parentObject?: unknown,
    rootActivity?:
      | ActivitySummary
      | ActivityTemplateSummary
      | MirroredActivitySummary,
    tagObject?: { topic?: boolean },
  ): SearchResult {
    const result: SearchResult = { found: false, matches: [] };

    // If the object is a string, check if it includes the searchText.
    if (typeof item === 'string') {
      if (shouldSkipProperty(key || '', path)) {
        return result;
      }
      if (item.toLowerCase().includes(searchText.toLowerCase())) {
        result.found = true;
        const category = determineCategory(path, key, tagObject);
        const objectInfo = rootActivity
          ? getObjectInfo(path, rootActivity)
          : { objectId: undefined, objectTitle: undefined };

        result.matches.push({
          property: key || path || 'content',
          text: item,
          category,
          objectPath: path,
          ...objectInfo,
        });
      }

      return result;
    }

    // If the object is an array, and it's the "Tags" array, perform specific handling.
    if (Array.isArray(item) && key === 'tags') {
      for (const value of item) {
        const foundTagOrTopic = tags.value.find((p) => p.id === value);
        if (foundTagOrTopic) {
          const tagResult = recursiveSearchWithContext(
            foundTagOrTopic,
            searchText,
            'tag',
            `${path}.tag`,
            foundTagOrTopic,
            rootActivity,
            foundTagOrTopic,
          );
          if (tagResult.found) {
            result.found = true;
            // Determine category based on topic property
            const category = foundTagOrTopic.topic === true ? 'Topic' : 'Tag';
            const tagMatches = tagResult.matches.map((match) => ({
              ...match,
              category: category as 'Tag' | 'Topic',
              objectId: foundTagOrTopic.id,
              objectTitle: foundTagOrTopic.title,
            }));
            result.matches.push(...tagMatches);
          }
        }
      }

      return result;
    }

    // If the object is an array, and it's the "Links" array, perform specific handling.
    if (Array.isArray(item) && key === 'links') {
      for (const value of item) {
        const foundLink = links.value.find((p) => p.id === value);
        if (foundLink) {
          const linkResult = recursiveSearchWithContext(
            foundLink,
            searchText,
            'link',
            `${path}.link`,
            foundLink,
            rootActivity,
          );
          if (linkResult.found) {
            result.found = true;
            // Override category for link matches
            const linkMatches = linkResult.matches.map((match) => ({
              ...match,
              category: 'Link' as const,
              objectId: foundLink.id,
              objectTitle: foundLink.title,
            }));
            result.matches.push(...linkMatches);
          }
        }
      }

      return result;
    }

    // If the object is an array, and it's the "Documents" array, perform specific handling.
    if (
      Array.isArray(item) &&
      (key === 'documents' || key === 'documentsIds')
    ) {
      for (const value of item) {
        const foundDocument = documents.value.find((p) => p.id === value);
        if (foundDocument) {
          const documentResult = recursiveSearchWithContext(
            foundDocument,
            searchText,
            'document',
            `${path}.document`,
            foundDocument,
            rootActivity,
          );
          if (documentResult.found) {
            result.found = true;
            // Override category for document matches
            const documentMatches = documentResult.matches.map((match) => ({
              ...match,
              category: 'Document' as const,
              objectId: foundDocument.id,
              objectTitle: foundDocument.title,
            }));
            result.matches.push(...documentMatches);
          }
        }
      }

      return result;
    }

    // If the object is an array, iterate over its elements.
    if (Array.isArray(item)) {
      for (let i = 0; i < item.length; i++) {
        const newPath = path ? `${path}[${i}]` : `[${i}]`;
        const arrayResult = recursiveSearchWithContext(
          item[i],
          searchText,
          key,
          newPath,
          item[i],
          rootActivity,
          tagObject,
        );
        if (arrayResult.found) {
          result.found = true;
          result.matches.push(...arrayResult.matches);
        }
      }

      return result;
    }

    // If the object is an object (and not null), iterate over its keys.
    if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
      const itemRecord = item as Record<string, unknown>;
      for (const innerKey of Object.keys(itemRecord)) {
        // Skip properties that would cause circular references or false categorization
        if (shouldSkipProperty(innerKey, path)) {
          continue;
        }

        const newPath = path ? `${path}.${innerKey}` : innerKey;
        const objectResult = recursiveSearchWithContext(
          itemRecord[innerKey],
          searchText,
          innerKey,
          newPath,
          item,
          rootActivity,
          tagObject,
        );
        if (objectResult.found) {
          result.found = true;
          result.matches.push(...objectResult.matches);
        }
      }

      return result;
    }

    // For other data types, return false.
    return result;
  }

  function searchInActivitiesWithContext<
    T extends
      | ActivitySummary
      | ActivityTemplateSummary
      | MirroredActivitySummary,
  >(activities: T[], searchText: string): SearchMatch[] {
    const matchesMap = new Map<string, SearchMatch>();

    for (const activity of activities) {
      const searchResult = recursiveSearchWithContext(
        activity,
        searchText,
        undefined,
        '',
        undefined,
        activity,
      );
      if (searchResult.found) {
        // Group matches by activity ID and category
        for (const match of searchResult.matches) {
          const key = `${activity.id}-${match.category}`;
          const objectInfo = getObjectInfo(match.objectPath || '', activity);
          const objectTitle = objectInfo.objectTitle || match.objectTitle;

          if (matchesMap.has(key)) {
            // Activity and category combination already exists, add property match
            const existingMatch = matchesMap.get(key)!;
            const newProperty = formatPropertyName(match.property);

            // Check if this property is already included
            const isDuplicate = existingMatch.matches.some(
              (m) =>
                m.property === newProperty &&
                m.text === match.text &&
                m.objectTitle === objectTitle,
            );

            if (!isDuplicate) {
              existingMatch.matches.push({
                property: newProperty,
                text: match.text,
                objectTitle: objectTitle,
              });

              // Add objectTitle to the list if it's not already there
              if (
                objectTitle &&
                !existingMatch.objectTitles.includes(objectTitle)
              ) {
                existingMatch.objectTitles.push(objectTitle);
              }
            }
          } else {
            // Create new match entry
            matchesMap.set(key, {
              id: activity.id,
              isMirroredActivity: !!(
                'source_activity_id' in activity && activity.source_activity_id
              ),
              searchType: 'like',
              matches: [
                {
                  property: formatPropertyName(match.property),
                  text: match.text,
                  objectTitle: objectTitle,
                },
              ],
              title: getActivityTitle(activity),
              category: match.category,
              objectTitles: objectTitle ? [objectTitle] : [],
            });
          }
        }
      }
    }

    return Array.from(matchesMap.values());
  }

  function getActivityTitle(
    activity:
      | ActivitySummary
      | ActivityTemplateSummary
      | MirroredActivitySummary,
  ): string {
    return activity.title || 'Unnamed Activity';
  }

  /**
   * Main search function that performs LIKE search on project/template data
   */
  function performSearch(
    data: Project | ProjectMapTemplate,
    searchText: string,
    isTemplate: boolean,
  ): SearchMatch[] {
    let likeResults: SearchMatch[] = [];

    // Perform LIKE search
    if (isTemplate) {
      likeResults = searchInActivitiesWithContext(
        (data as ProjectMapTemplate).activities as ActivityTemplateSummary[],
        searchText,
      );
    } else {
      const projectData = data as Project;
      const detailedActivitiesResult = searchInActivitiesWithContext(
        projectData.activities as ActivitySummary[],
        searchText,
      );
      const detailedMirroredResult = searchInActivitiesWithContext(
        projectData.mirrored_activities as MirroredActivitySummary[],
        searchText,
      );
      likeResults = [...detailedActivitiesResult, ...detailedMirroredResult];
    }

    return likeResults;
  }

  return {
    performSearch,
  };
}
